import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { Body1, Body5, Container, Dialog, DialogButtonRow, TextField } from '@ui';
import { FlatButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { ROUTES } from '@constants/routes';
import { showSuccessBanner } from '@utils/application';
import { dismissUnmappedRow, fetchRun, fetchUnmappedRow } from '@services/fmdc';
import { Chip } from '@features/scoreSpec/styles';
import { DefList } from './DefList';
import { StatusPill } from './StatusPill';
import { ConfidenceDot } from './ConfidenceDot';
import { MapForm } from './MapForm';
import { HistoryList } from './HistoryList';
import { RerunConfirm } from './RerunConfirm';
import { NoticeText, NOTICE_TONES } from './NoticeText';
import { fmdcPaths } from './fmdcUrl';
import { EM_DASH, formatUnmappedReason, humanizeToken } from './format';
import { DangerTransparentButton, Mono, SectionHead } from './styles';

// A row the extractor could not place: map it to a catalog item, accept the code it proposed, or
// dismiss it. GET /unmapped_rows/:id carries the row, its candidate items, whether it is a
// leftover (the rest of the sheet row is mapped and only these cells have no period header), and
// its history.

const DECISION_TOKENS = {
  mapped: 'positive',
  approved: 'positive',
  dismissed: 'negative',
  rejected: 'negative',
};

// The dismiss confirm. A ConfirmModal's body is a Body5, which cannot hold the reason field, so
// this is the same Dialog with the field folded in (the RerunConfirm shape).
const DismissConfirm = ({ reason, onReasonChange, busy, error, onConfirm, onCancel }) => {
  const title = 'Dismiss this row?';
  return (
    <Dialog label={title} onDismiss={onCancel} maxWidth="480px">
      <Body1 bold>{title}</Body1>
      <Container top={12}>
        <Body5>Later runs of this issuer keep this row unmapped and out of the queue.</Body5>
      </Container>
      <Container top={12}>
        <TextField label="Reason" value={reason} onChange={onReasonChange} placeholder="Optional" />
      </Container>
      {error && (
        <Container top={8}>
          <ErrorText>{error}</ErrorText>
        </Container>
      )}
      <Container top={24}>
        <DialogButtonRow>
          <TransparentFlatButton onClick={onCancel} disabled={busy}>
            Cancel
          </TransparentFlatButton>
          <FlatButton onClick={onConfirm} disabled={busy}>
            {busy ? 'Dismissing…' : 'Dismiss row'}
          </FlatButton>
        </DialogButtonRow>
      </Container>
    </Dialog>
  );
};

const decisionPill = (decision) => {
  if (!decision) return <StatusPill token="secondaryText" label="Pending" />;
  return <StatusPill token={DECISION_TOKENS[decision] || 'secondaryText'} label={humanizeToken(decision)} />;
};

export const UnmappedDetailView = ({ runId, unmappedId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [detail, setDetail] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [mappedNotices, setMappedNotices] = React.useState(null);
  const [confirmingDismiss, setConfirmingDismiss] = React.useState(false);
  const [dismissReason, setDismissReason] = React.useState('');
  const [dismissBusy, setDismissBusy] = React.useState(false);
  const [dismissError, setDismissError] = React.useState(null);
  const [rerunRun, setRerunRun] = React.useState(null);
  const [rerunLoading, setRerunLoading] = React.useState(false);
  const [rerunError, setRerunError] = React.useState(null);

  const load = React.useCallback(
    async (state) => {
      setError(null);
      const result = await fetchUnmappedRow(unmappedId);
      if (state?.isCanceled) return;
      setIsLoading(false);
      if (result.error) {
        setError(result.error);
        return;
      }
      setDetail(result.detail);
    },
    [unmappedId]
  );

  React.useEffect(() => {
    const state = { isCanceled: false };
    setIsLoading(true);
    load(state);
    return () => {
      state.isCanceled = true;
    };
  }, [load]);

  const handleMapped = (result, notices) => {
    setMappedNotices([
      { key: 'rerun', tone: NOTICE_TONES.POSITIVE, text: 'Mapped. The values appear after a rerun.' },
      ...(notices || []),
    ]);
    if (result?.row) setDetail((prev) => ({ ...prev, row: { ...prev?.row, ...result.row } }));
    else load();
  };

  const openRerun = async () => {
    if (rerunLoading) return;
    setRerunLoading(true);
    setRerunError(null);
    const result = await fetchRun(runId);
    setRerunLoading(false);
    if (result.error) {
      setRerunError(result.error);
      return;
    }
    setRerunRun(result.run);
  };

  const handleRerunDone = () => {
    setRerunRun(null);
    showSuccessBanner('Rerun started', null, dispatch);
    navigate(fmdcPaths.run(ROUTES.fmdc, runId));
  };

  const dismiss = async () => {
    if (dismissBusy || !detail?.row) return;
    setDismissBusy(true);
    setDismissError(null);
    const result = await dismissUnmappedRow(detail.row.id, { reason: dismissReason.trim() || undefined });
    setDismissBusy(false);
    if (result.error) {
      setDismissError(result.error);
      return;
    }
    setConfirmingDismiss(false);
    setDismissReason('');
    showSuccessBanner('Row dismissed', null, dispatch);
    load();
  };

  if (isLoading) {
    return <Body5 isLowContrast>Loading row…</Body5>;
  }

  if (error || !detail?.row) {
    return (
      <div>
        <Body5>{error || 'This row could not be read.'}</Body5>
        <Container top={12}>
          <SkeletonButton onClick={() => load()}>Retry</SkeletonButton>
        </Container>
      </div>
    );
  }

  const { row, leftover } = detail;
  const candidateItems = detail.candidate_items || [];
  const isDecided = !!row.reviewer_decision;
  const alreadyDismissed = isDecided && row?.reviewer_decision === 'dismissed';

  const identityRows = [
    { label: 'Sheet / range', value: row.source_cell_or_range ? <Mono>{row.source_cell_or_range}</Mono> : null },
    { label: 'Values', value: row.source_row_values_preview },
    { label: 'Reason unmapped', value: row.reason_unmapped ? formatUnmappedReason(row.reason_unmapped) : null },
    {
      label: 'Blocking',
      value:
        row.is_blocking === true ? (
          <Chip $tone="bad">Blocking</Chip>
        ) : row.is_blocking === false ? (
          <Chip>Not blocking</Chip>
        ) : null,
    },
    { label: 'Confidence', value: row.confidence ? <ConfidenceDot confidence={row.confidence} /> : null },
    { label: 'Candidate code', value: row.candidate_item_code ? <Mono>{row.candidate_item_code}</Mono> : null },
    {
      label: 'Proposed code',
      value: row.proposed_new_catalog_code ? <Mono>{row.proposed_new_catalog_code}</Mono> : null,
    },
    { label: 'Notes', value: row.notes },
    { label: 'Decision', value: decisionPill(row.reviewer_decision) },
  ];

  return (
    <div>
      <Body1 bold>{row.source_row_label_raw || row.unmapped_id || EM_DASH}</Body1>
      {row.unmapped_id && (
        <Container top={4}>
          <Mono>{row.unmapped_id}</Mono>
        </Container>
      )}
      <Container top={16}>
        <DefList rows={identityRows} />
      </Container>

      {leftover && (
        <Container top={16}>
          <NoticeText tone={NOTICE_TONES.NEUTRAL}>
            The rest of this row is mapped; only these cells have no period header. They can be dismissed, not mapped.
          </NoticeText>
        </Container>
      )}

      {mappedNotices && (
        <Container top={16}>
          {mappedNotices.map((notice) => (
            <Container key={notice.key} bottom={8}>
              <NoticeText tone={notice.tone}>{notice.text}</NoticeText>
            </Container>
          ))}
          <Container top={4} row verticallyCenter gap={12} flexWrap>
            <SkeletonButton onClick={openRerun} disabled={rerunLoading}>
              {rerunLoading ? 'Opening…' : 'Rerun now'}
            </SkeletonButton>
            {rerunError && <ErrorText>{rerunError}</ErrorText>}
          </Container>
        </Container>
      )}

      {!leftover && !mappedNotices && (
        <Container top={24}>
          <MapForm row={row} candidateItems={candidateItems} onMapped={handleMapped} />
        </Container>
      )}

      <Container top={24}>
        <SectionHead
          heading="Dismiss"
          subtext="For check rows and memos. Later runs of this issuer keep this row unmapped and out of the queue."
        />
        <Container top={12} row verticallyCenter gap={12} flexWrap>
          <DangerTransparentButton
            onClick={() => {
              // `disabled` on the shared buttons is styling only; the guard lives here.
              if (alreadyDismissed) return;
              setConfirmingDismiss(true);
            }}
            disabled={alreadyDismissed}
          >
            Dismiss row
          </DangerTransparentButton>
          {dismissError && !confirmingDismiss && <ErrorText>{dismissError}</ErrorText>}
        </Container>
      </Container>

      <Container top={24}>
        <HistoryList events={detail.history || []} />
      </Container>

      {confirmingDismiss && (
        <DismissConfirm
          reason={dismissReason}
          onReasonChange={setDismissReason}
          busy={dismissBusy}
          error={dismissError}
          onConfirm={dismiss}
          onCancel={() => {
            if (dismissBusy) return;
            setConfirmingDismiss(false);
            setDismissError(null);
          }}
        />
      )}

      {rerunRun && <RerunConfirm run={rerunRun} onDone={handleRerunDone} onCancel={() => setRerunRun(null)} />}
    </div>
  );
};
